import React, { useState, useRef } from 'react';
import ClearFilterIcon from '../../../../../../svg/icons/remove.svg';
import BaguetteIcon from '../../../../../../svg/icons/diamond-cuts/baguette.svg';
import CushionIcon from '../../../../../../svg/icons/diamond-cuts/cushion.svg';
import EmeraldIcon from '../../../../../../svg/icons/diamond-cuts/emerald.svg';
import HeartIcon from '../../../../../../svg/icons/diamond-cuts/heart.svg';
import MarquiseIcon from '../../../../../../svg/icons/diamond-cuts/marquise.svg';
import OvalIcon from '../../../../../../svg/icons/diamond-cuts/oval.svg';
import PearIcon from '../../../../../../svg/icons/diamond-cuts/pear.svg';
import PrincessIcon from '../../../../../../svg/icons/diamond-cuts/princess.svg';
import RadiantIcon from '../../../../../../svg/icons/diamond-cuts/radiant.svg';
import RoundBrilliantIcon from '../../../../../../svg/icons/diamond-cuts/round-brilliant.svg';
import SingleCutIcon from '../../../../../../svg/icons/diamond-cuts/single-cut.svg';
import standardFacetStyles from './standardFacet.module.pcss';
import { FacetData, FacetValueData, MessageProperties } from '../../../../types';

interface StandardFacetProps {
  facet: FacetData;
  isDisabled: boolean;
  isIconsEnabled: boolean;
  isColoursEnabled: boolean;
  isFilterEnabled: boolean;
  facetSelectionHandler: (facetValue: FacetValueData, facetData: FacetData) => void;
  messages: MessageProperties;
}

function StandardFacet({
  facet,
  isDisabled,
  isIconsEnabled,
  isColoursEnabled,
  isFilterEnabled,
  facetSelectionHandler,
  messages
}: StandardFacetProps): React.ReactElement {
  const [filterValue, setFilterValue] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const filteredFacetValues =
    isFilterEnabled && filterValue
      ? facet.values.filter((value) => value.name.toLowerCase().includes(filterValue.toLowerCase()))
      : facet.values;

  function filterHandler(event: React.ChangeEvent<HTMLInputElement>): void {
    setFilterValue(event.target.value);
  }

  function clearInput(): void {
    setFilterValue('');
    ref.current?.focus();
  }

  return (
    <div
      className={`${standardFacetStyles['standard-facet']} ${
        isFilterEnabled ? standardFacetStyles['standard-facet--wide'] : ''
      }`}
    >
      {isFilterEnabled && (
        <>
          <div className={standardFacetStyles['standard-facet__filter']}>
            <input
              ref={ref}
              type="text"
              disabled={isDisabled}
              value={filterValue}
              onChange={filterHandler}
              placeholder={messages.placeholder}
              className={standardFacetStyles['standard-facet__filter-input']}
            />
            <button
              className={standardFacetStyles['standard-facet__clear-filter-button']}
              onClick={clearInput}
              type="button"
            >
              <ClearFilterIcon className={standardFacetStyles['standard-facet__clear-filter-icon']} />
            </button>
          </div>
          {filteredFacetValues.length === 0 && (
            <p className={standardFacetStyles['standard-facet__filter-no-results']}>{messages.noResults}</p>
          )}
        </>
      )}
      <ul className={standardFacetStyles['standard-facet__list']}>
        {filteredFacetValues.map((value) => (
          <li key={value.code}>
            <button
              type="button"
              disabled={(value.disabled && !value.selected) || isDisabled}
              className={`${standardFacetStyles['standard-facet__list-item-title']} ${
                value.disabled && !value.selected
                  ? standardFacetStyles['standard-facet__list-item-title--disabled']
                  : ''
              }`}
              onClick={(): void => facetSelectionHandler(value, facet)}
            >
              <span
                className={`${standardFacetStyles['standard-facet__list-item-title-circle']} ${
                  value.selected ? standardFacetStyles['standard-facet__list-item-title-circle--active'] : ''
                }`}
              />
              <span className={standardFacetStyles['standard-facet__list-item-title-text']}>
                {value.name.toLowerCase()}
                {isColoursEnabled && getColourIconComponent(standardFacetStyles, value.name)}
                {isIconsEnabled && getSVGIconComponent(standardFacetStyles, value.name)}
                <span className={standardFacetStyles['standard-facet__list-item-title-text-count']}>{value.count}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function getColourIconComponent(styles: { [className: string]: string }, facetName: string): React.ReactElement | null {
  const colourIconsMap = {
    beige: styles['standard-facet__list-item-title-text-icon-colour--beige'],
    black: styles['standard-facet__list-item-title-text-icon-colour--black'],
    blue: styles['standard-facet__list-item-title-text-icon-colour--blue'],
    blush: styles['standard-facet__list-item-title-text-icon-colour--blush'],
    bronze: styles['standard-facet__list-item-title-text-icon-colour--bronze'],
    brown: styles['standard-facet__list-item-title-text-icon-colour--brown'],
    burgundy: styles['standard-facet__list-item-title-text-icon-colour--burgundy'],
    clear: styles['standard-facet__list-item-title-text-icon-colour--clear'],
    champagne: styles['standard-facet__list-item-title-text-icon-colour--champagne'],
    cream: styles['standard-facet__list-item-title-text-icon-colour--cream'],
    gold: styles['standard-facet__list-item-title-text-icon-colour--gold'],
    green: styles['standard-facet__list-item-title-text-icon-colour--green'],
    grey: styles['standard-facet__list-item-title-text-icon-colour--grey'],
    ivory: styles['standard-facet__list-item-title-text-icon-colour--ivory'],
    orange: styles['standard-facet__list-item-title-text-icon-colour--orange'],
    pink: styles['standard-facet__list-item-title-text-icon-colour--pink'],
    purple: styles['standard-facet__list-item-title-text-icon-colour--purple'],
    red: styles['standard-facet__list-item-title-text-icon-colour--red'],
    silver: styles['standard-facet__list-item-title-text-icon-colour--silver'],
    white: styles['standard-facet__list-item-title-text-icon-colour--white'],
    yellow: styles['standard-facet__list-item-title-text-icon-colour--yellow'],
    'mother-of-pearl': styles['standard-facet__list-item-title-text-icon-colour--mother-of-pearl'],
    'multi-coloured': styles['standard-facet__list-item-title-text-icon-colour--multi-coloured'],
    'rose-gold': styles['standard-facet__list-item-title-text-icon-colour--rose-gold'],
    'rose-tone': styles['standard-facet__list-item-title-text-icon-colour--rose-tone']
  };
  const colourIconClassName = colourIconsMap[facetName.toLowerCase().replace(/\s/g, '-')];
  return colourIconClassName ? (
    <span className={`${styles['standard-facet__list-item-title-text-icon-colour']} ${colourIconClassName}`} />
  ) : null;
}

function getSVGIconComponent(styles: { [className: string]: string }, facetName: string): React.ReactElement | null {
  const svgIconsMap = {
    baguette: BaguetteIcon,
    cushion: CushionIcon,
    emerald: EmeraldIcon,
    heart: HeartIcon,
    marquise: MarquiseIcon,
    oval: OvalIcon,
    pear: PearIcon,
    princess: PrincessIcon,
    radiant: RadiantIcon,
    'round-brilliant': RoundBrilliantIcon,
    'single-cut': SingleCutIcon
  };
  const SVGIconComponent = svgIconsMap[facetName.toLowerCase().replace(' ', '-')];
  return SVGIconComponent ? (
    <SVGIconComponent className={styles['standard-facet__list-item-title-text-icon-shape']} />
  ) : null;
}

export default StandardFacet;
